import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Grid, IconButton, Alert, Avatar,
  FormControl, InputLabel, Select, MenuItem, Chip, Divider,
  Tooltip, CircularProgress,
} from '@mui/material';
import {
  Add, Edit, Delete, Description as DocIcon,
  CloudUpload, Download, Visibility, Close,
} from '@mui/icons-material';
import api from '../services/api';
import { toast } from 'react-hot-toast';

const DOCUMENT_TYPES = [
  { value: 'family_photo', label: 'Family Photo' },
  { value: 'aadhaar', label: 'Aadhaar Card' },
  { value: 'pan', label: 'PAN Card' },
  { value: 'esi', label: 'ESI Document' },
  { value: 'pf', label: 'PF Document' },
  { value: 'bank_passbook', label: 'Bank Passbook' },
  { value: 'experience_letter', label: 'Experience Letter' },
  { value: 'education_certificate', label: 'Education Certificate' },
  { value: 'medical_fitness', label: 'Medical Fitness Certificate' },
  { value: 'police_verification', label: 'Police Verification' },
  { value: 'relieving_letter', label: 'Relieving Letter' },
  { value: 'address_proof', label: 'Address Proof' },
  { value: 'other', label: 'Other' },
];

const docTypeLabel = (value) =>
  DOCUMENT_TYPES.find((d) => d.value === value)?.label || value;

const HR = () => {
  const [employees, setEmployees] = useState([]);
  const [openEmployeeDialog, setOpenEmployeeDialog] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [employeeForm, setEmployeeForm] = useState({
    employee_id: '', designation: '', department: '',
    joining_date: '', salary: 0, bank_account: '',
    pf_number: '', esi_number: '',
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  // Documents dialog state
  const [openDocDialog, setOpenDocDialog] = useState(false);
  const [docEmployee, setDocEmployee] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [docLoading, setDocLoading] = useState(false);
  const [uploadDocType, setUploadDocType] = useState('aadhaar');
  const [uploadDocTitle, setUploadDocTitle] = useState('');
  const [uploadDocFile, setUploadDocFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Preview dialog
  const [openPreview, setOpenPreview] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/hr/employees/');
      const data = Array.isArray(response.data) ? response.data
        : response.data?.results || [];
      setEmployees(data);
      if (data.length === 0) setError('No employees found. Click "Add Employee" to create one.');
    } catch (err) {
      console.error('Error fetching employees:', err);
      setError('Failed to fetch employees. Please try again.');
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  // ---------- Employee CRUD ----------

  const openAddDialog = () => {
    setEditingEmployee(null);
    setEmployeeForm({
      employee_id: '', designation: '', department: '',
      joining_date: new Date().toISOString().split('T')[0],
      salary: 0, bank_account: '', pf_number: '', esi_number: '',
    });
    setPhotoFile(null);
    setPhotoPreview(null);
    setOpenEmployeeDialog(true);
  };

  const openEditDialog = (employee) => {
    setEditingEmployee(employee);
    setEmployeeForm({
      employee_id: employee.employee_id || '',
      designation: employee.designation || '',
      department: employee.department || '',
      joining_date: employee.joining_date || '',
      salary: parseFloat(employee.salary) || 0,
      bank_account: employee.bank_account || '',
      pf_number: employee.pf_number || '',
      esi_number: employee.esi_number || '',
    });
    setPhotoFile(null);
    setPhotoPreview(employee.photo_url || null);
    setOpenEmployeeDialog(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEmployeeForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Photo must be an image file');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Photo must be less than 2 MB');
      return;
    }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleEmployeeSubmit = async () => {
    if (!employeeForm.employee_id) return toast.error('Employee ID is required');
    if (!employeeForm.designation) return toast.error('Designation is required');
    if (!editingEmployee && !photoFile && !photoPreview) {
      return toast.error('Employee photo is required');
    }

    try {
      const fd = new FormData();
      fd.append('employee_id', employeeForm.employee_id);
      fd.append('designation', employeeForm.designation);
      fd.append('department', employeeForm.department || '');
      fd.append('joining_date', employeeForm.joining_date || new Date().toISOString().split('T')[0]);
      fd.append('salary', parseFloat(employeeForm.salary) || 0);
      fd.append('bank_account', employeeForm.bank_account || '');
      fd.append('pf_number', employeeForm.pf_number || '');
      fd.append('esi_number', employeeForm.esi_number || '');
      if (photoFile) fd.append('photo', photoFile);

      if (editingEmployee) {
        await api.put(`/hr/employees/${editingEmployee.id}/`, fd);
        toast.success('Employee updated successfully');
      } else {
        await api.post('/hr/employees/', fd);
        toast.success('Employee added successfully');
      }
      setOpenEmployeeDialog(false);
      fetchEmployees();
    } catch (err) {
      console.error('Error saving employee:', err);
      console.error('Response data:', JSON.stringify(err.response?.data, null, 2));
      const msg = err.response?.data
        ? JSON.stringify(err.response.data)
        : 'Failed to save employee';
      toast.error(msg);
    }
  };

  const handleEmployeeDelete = async (id) => {
    if (!window.confirm('Delete this employee?')) return;
    try {
      await api.delete(`/hr/employees/${id}/`);
      toast.success('Employee deleted successfully');
      fetchEmployees();
    } catch (err) {
      toast.error('Failed to delete employee');
    }
  };

  // ---------- Documents ----------

  const openDocumentsDialog = async (employee) => {
    setDocEmployee(employee);
    setDocuments([]);
    setUploadDocType('aadhaar');
    setUploadDocTitle('');
    setUploadDocFile(null);
    setOpenDocDialog(true);
    await fetchDocuments(employee.id);
  };

  const fetchDocuments = async (employeeId) => {
    setDocLoading(true);
    try {
      const res = await api.get(`/hr/employees/${employeeId}/documents/`);
      const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
      setDocuments(data);
    } catch (err) {
      console.error('Error fetching documents:', err);
      toast.error('Failed to load documents');
    } finally {
      setDocLoading(false);
    }
  };

  const handleUploadDocument = async () => {
    if (!uploadDocFile) return toast.error('Please select a file');
    if (!uploadDocTitle.trim()) return toast.error('Please enter a title');

    try {
      setUploading(true);
      const fd = new FormData();
      fd.append('document_type', uploadDocType);
      fd.append('title', uploadDocTitle.trim());
      fd.append('file', uploadDocFile);

      // Let axios set the multipart Content-Type (with boundary) automatically.
      await api.post(`/hr/employees/${docEmployee.id}/documents/`, fd);

      toast.success('Document uploaded successfully');
      setUploadDocTitle('');
      setUploadDocFile(null);
      const fileInput = document.getElementById('doc-file-input');
      if (fileInput) fileInput.value = '';

      await fetchDocuments(docEmployee.id);
      fetchEmployees();
    } catch (err) {
      console.error('Error uploading document:', err);
      console.error('Response data:', JSON.stringify(err.response?.data, null, 2));
      const msg = err.response?.data
        ? JSON.stringify(err.response.data)
        : 'Upload failed';
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await api.delete(`/hr/documents/${docId}/`);
      toast.success('Document deleted');
      await fetchDocuments(docEmployee.id);
      fetchEmployees();
    } catch (err) {
      toast.error('Failed to delete document');
    }
  };

  const openPreviewDialog = (doc) => {
    setPreviewDoc(doc);
    setOpenPreview(true);
  };

  const handleDownload = (doc) => {
    if (!doc.file_url) return toast.error('File not available');
    const a = document.createElement('a');
    a.href = doc.file_url;
    a.download = doc.file_name || 'download';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const isImage = (name = '') => /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(name);
  const isPdf = (name = '') => /\.pdf$/i.test(name);

  const formatBytes = (bytes) => {
    if (!bytes) return '';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(2)} MB`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Human Resources</Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Employees</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openAddDialog}>
          Add Employee
        </Button>
      </Box>

      {error && (
        <Alert severity={error.includes('No employees') ? 'info' : 'error'} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Photo</TableCell>
                <TableCell>Employee ID</TableCell>
                <TableCell>Designation</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Joining Date</TableCell>
                <TableCell align="right">Salary</TableCell>
                <TableCell align="center">Documents</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {employees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="textSecondary" sx={{ py: 3 }}>
                      No employees found. Click "Add Employee" to create one.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : employees.map((employee) => (
                <TableRow key={employee.id}>
                  <TableCell>
                    <Avatar
                      src={employee.photo_url || undefined}
                      alt={employee.employee_id}
                      sx={{ width: 44, height: 44 }}
                    >
                      {employee.employee_id?.[0] || '?'}
                    </Avatar>
                  </TableCell>
                  <TableCell>{employee.employee_id}</TableCell>
                  <TableCell>{employee.designation}</TableCell>
                  <TableCell>{employee.department || 'N/A'}</TableCell>
                  <TableCell>
                    {employee.joining_date
                      ? new Date(employee.joining_date).toLocaleDateString('en-IN')
                      : 'N/A'}
                  </TableCell>
                  <TableCell align="right">
                    ₹{parseFloat(employee.salary || 0).toFixed(2)}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      icon={<DocIcon />}
                      label={employee.documents_count || 0}
                      size="small"
                      color={employee.documents_count > 0 ? 'primary' : 'default'}
                      onClick={() => openDocumentsDialog(employee)}
                      clickable
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEditDialog(employee)}>
                      <Edit />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleEmployeeDelete(employee.id)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Employee Dialog */}
      <Dialog open={openEmployeeDialog} onClose={() => setOpenEmployeeDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingEmployee ? 'Edit Employee' : 'Add Employee'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={3} flexWrap="wrap">
                <Avatar
                  src={photoPreview || undefined}
                  sx={{ width: 100, height: 100, border: '2px solid #ddd' }}
                >
                  {employeeForm.employee_id?.[0] || '?'}
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Employee Photo <span style={{ color: '#d32f2f' }}>*</span>
                  </Typography>
                  <input
                    accept="image/*"
                    id="employee-photo-input"
                    type="file"
                    style={{ display: 'none' }}
                    onChange={handlePhotoChange}
                  />
                  <label htmlFor="employee-photo-input">
                    <Button variant="outlined" component="span" startIcon={<CloudUpload />}>
                      {photoPreview ? 'Change Photo' : 'Upload Photo'}
                    </Button>
                  </label>
                  <Typography variant="caption" display="block" color="textSecondary" sx={{ mt: 1 }}>
                    JPG/PNG, max 2 MB. Required for new employees.
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12}><Divider /></Grid>

            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Employee ID" name="employee_id"
                value={employeeForm.employee_id} onChange={handleInputChange}
                required helperText="Unique employee identifier" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Designation" name="designation"
                value={employeeForm.designation} onChange={handleInputChange}
                required helperText="e.g., Manager, Operator, Supervisor" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Department" name="department"
                value={employeeForm.department} onChange={handleInputChange}
                helperText="e.g., Production, Sales, Admin" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Joining Date" name="joining_date" type="date"
                value={employeeForm.joining_date} onChange={handleInputChange}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Salary (₹)" name="salary" type="number"
                value={employeeForm.salary} onChange={handleInputChange}
                InputProps={{ inputProps: { min: 0 } }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Bank Account" name="bank_account"
                value={employeeForm.bank_account} onChange={handleInputChange} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="PF Number" name="pf_number"
                value={employeeForm.pf_number} onChange={handleInputChange} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="ESI Number" name="esi_number"
                value={employeeForm.esi_number} onChange={handleInputChange} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEmployeeDialog(false)}>Cancel</Button>
          <Button onClick={handleEmployeeSubmit} variant="contained">
            {editingEmployee ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Documents Dialog */}
      <Dialog open={openDocDialog} onClose={() => setOpenDocDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar src={docEmployee?.photo_url || undefined} sx={{ width: 40, height: 40 }}>
              {docEmployee?.employee_id?.[0] || '?'}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Documents
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {docEmployee?.employee_id} — {docEmployee?.designation}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>

          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Upload New Document
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Document Type</InputLabel>
                  <Select value={uploadDocType}
                    onChange={(e) => setUploadDocType(e.target.value)}
                    label="Document Type">
                    {DOCUMENT_TYPES.map((d) => (
                      <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="Title"
                  value={uploadDocTitle}
                  onChange={(e) => setUploadDocTitle(e.target.value)}
                  placeholder="e.g., Front side" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <input
                  id="doc-file-input"
                  type="file"
                  accept="image/*,.pdf,.doc,.docx"
                  style={{ display: 'none' }}
                  onChange={(e) => setUploadDocFile(e.target.files?.[0] || null)}
                />
                <label htmlFor="doc-file-input">
                  <Button variant="outlined" component="span" startIcon={<CloudUpload />} fullWidth>
                    {uploadDocFile ? uploadDocFile.name.slice(0, 20) + '…' : 'Choose File'}
                  </Button>
                </label>
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" onClick={handleUploadDocument}
                  disabled={uploading || !uploadDocFile || !uploadDocTitle.trim()}>
                  {uploading ? 'Uploading…' : 'Upload Document'}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {docLoading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : documents.length === 0 ? (
            <Typography color="textSecondary" align="center" sx={{ py: 4 }}>
              No documents uploaded yet.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>File</TableCell>
                    <TableCell>Uploaded</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documents.map((doc) => (
                    <TableRow key={doc.id} hover>
                      <TableCell>
                        <Chip label={doc.document_type_display || docTypeLabel(doc.document_type)}
                          size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{doc.title}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{doc.file_name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {formatBytes(doc.file_size)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleString('en-IN') : 'N/A'}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Preview">
                          <IconButton size="small" color="primary"
                            onClick={() => openPreviewDialog(doc)}>
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download">
                          <IconButton size="small" color="success"
                            onClick={() => handleDownload(doc)}>
                            <Download />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error"
                            onClick={() => handleDeleteDocument(doc.id)}>
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDocDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={openPreview} onClose={() => setOpenPreview(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {previewDoc?.title}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {previewDoc?.document_type_display} — {previewDoc?.file_name}
              </Typography>
            </Box>
            <IconButton onClick={() => setOpenPreview(false)}><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ textAlign: 'center', bgcolor: '#f5f5f5' }}>
          {previewDoc && isImage(previewDoc.file_name) && (
            <img
              src={previewDoc.file_url}
              alt={previewDoc.title}
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
          )}
          {previewDoc && isPdf(previewDoc.file_name) && (
            <iframe
              src={previewDoc.file_url}
              title={previewDoc.title}
              style={{ width: '100%', height: '70vh', border: 'none', background: '#fff' }}
            />
          )}
          {previewDoc && !isImage(previewDoc.file_name) && !isPdf(previewDoc.file_name) && (
            <Box py={4}>
              <Typography color="textSecondary" gutterBottom>
                Preview not available for this file type.
              </Typography>
              <Button variant="contained" startIcon={<Download />}
                onClick={() => handleDownload(previewDoc)}>
                Download to View
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button startIcon={<Download />} onClick={() => handleDownload(previewDoc)}>Download</Button>
          <Button onClick={() => setOpenPreview(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HR;